import { useEffect, useState } from "react";
import axios from "axios";
import {
  Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Divider, InputAdornment, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AllocationManager from "./AllocationManager";
import PersonnelContractPanel from "./PersonnelContractPanel";
import { API_BASE_URL } from "../config";
import { ATTENDANCE_ROLES, ATTENDANCE_STATUSES } from "../constants";
import { useMe } from "../MeContext";
import { notify } from "../notify";

const ATT_API = `${API_BASE_URL}/attendances`;
const TEAMS_API = `${API_BASE_URL}/event-teams`;

const toForm = (a) => ({
  role: a?.role ?? "",
  status: a?.status ?? "",
  fee: a?.fee == null ? "" : String(a.fee),
  shift_details: a?.shift_details ?? "",
  availability_notes: a?.availability_notes ?? "",
  // "" = not in a team. Teams are created on the event itself (Events page).
  event_team_id: a?.event_team_id == null ? "" : String(a.event_team_id),
});

/**
 * Edit one person's attendance on one event — their role and status, plus the
 * arrangements personal to them (fee, shift details) that they see on their own
 * "My events" view. Shared by the admin Events and Personnel pages, which each
 * show the other side of the same join row.
 *
 * Props:
 *  - attendance: the row being edited ({id, role, status, fee, shift_details})
 *  - subject:    who/what this attendance is for, shown in the title
 *                (a person's name on the Events page, an event on Personnel)
 *  - onClose:    () => void
 *  - onSaved:    () => void — called after a successful save or delete
 *  - canEdit / canDelete
 */
export default function AttendanceDialog({ attendance, subject, onClose, onSaved,
                                          canEdit, canDelete }) {
  // The allocations section below has its own permissions (the inventory ones), so
  // this dialog reads them itself rather than taking two more props from each of
  // its two callers.
  const { can } = useMe();
  const open = attendance !== null && attendance !== undefined;
  const [form, setForm] = useState(toForm(attendance));
  const [initial, setInitial] = useState(toForm(attendance));
  const [shownSubject, setShownSubject] = useState(subject);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Engagement schedule (Contracts.personnel): what the contract section reports,
  // and the offer to e-sign made when the status is moved to Contract Sent.
  const canContract = can("Contracts.personnel");
  const [contractState, setContractState] = useState(null);
  const [offerContract, setOfferContract] = useState(false);
  const [startContract, setStartContract] = useState(0);

  // Load the form whenever an attendance is opened. Deliberately skipped when it
  // goes null (on close), so the content doesn't blank out mid fade-out.
  useEffect(() => {
    if (!attendance) return;
    const loaded = toForm(attendance);
    setForm(loaded);
    setInitial(loaded);
    setShownSubject(subject);
    setConfirmClose(false);
    setConfirmDelete(false);
    setContractState(null);
    setOfferContract(false);
    // The event's teams, to offer as this person's team. Fetched here rather than
    // passed in, so both callers (Events and Personnel) get it from the row's own
    // event_id. An empty list just means the event has no teams set up.
    if (attendance.event_id) {
      axios.get(TEAMS_API + "/", { params: { event_id: attendance.event_id } })
        .then(res => setTeams(res.data || []))
        .catch(() => setTeams([]));
    } else {
      setTeams([]);
    }
    // `subject` is derived from `attendance` by the caller — reload on the row only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const isDirty = () => JSON.stringify(form) !== JSON.stringify(initial);
  const requestClose = () => (canEdit && isDirty() ? setConfirmClose(true) : onClose());

  const put = () => axios.put(`${ATT_API}/${attendance.id}`, {
    role: form.role || null,
    status: form.status || null,
    fee: form.fee === "" ? null : Number(form.fee),
    shift_details: form.shift_details.trim() || null,
    availability_notes: form.availability_notes.trim() || null,
    event_team_id: form.event_team_id === "" ? null : Number(form.event_team_id),
  });

  // Before any contract action: the engagement schedule is merged from the
  // *saved* attendance, so unsaved edits to the role, fee or shifts would
  // silently be left out of it. Save them first. Resolves to whether anything was
  // saved (so a draft merged earlier knows to be merged again); rejects if the
  // save failed, so the action doesn't go ahead without the edits.
  const saveBeforeContract = () => {
    if (!canEdit || !isDirty()) return Promise.resolve(false);
    if (feeError) {
      notify("Fix the fee before preparing the contract.", "error");
      return Promise.reject(new Error("invalid fee"));
    }
    setBusy(true);
    return put().then(() => {
      notify("Your changes to this attendance were saved first.", "info");
      setInitial(form);
      onSaved();
      return true;
    }).finally(() => setBusy(false));
  };

  const save = () => {
    setBusy(true);
    put().then(() => {
      notify("Event details saved.", "success");
      onSaved();
      // Moving someone to Contract Sent is the moment to send the engagement
      // schedule — offered, not forced, since some contracts still go on paper.
      const movedToSent = form.status === "Contract Sent" && initial.status !== "Contract Sent";
      if (movedToSent && canContract && contractState?.hasTemplate && !contractState.active) {
        setInitial(form);
        setOfferContract(true);
      } else {
        onClose();
      }
    }).finally(() => setBusy(false));
  };

  // Sending sets the attendance to Contract Sent on the server; keep the form in
  // step, or a later Save here would put the old status back.
  const contractSent = () => {
    onSaved();
    const sync = (f) => (f.status === "Contract Signed" ? f : { ...f, status: "Contract Sent" });
    setForm(sync);
    setInitial(sync);
  };

  const doDelete = () => {
    setBusy(true);
    axios.delete(`${ATT_API}/${attendance.id}`).then(() => {
      notify("Removed from the event.", "success");
      setConfirmDelete(false);
      onSaved();
      onClose();
    }).finally(() => setBusy(false));
  };

  // A fee is money: reject anything that isn't a non-negative number.
  const feeError = form.fee !== "" && !(Number(form.fee) >= 0);

  return (
    <>
      <Dialog open={open} onClose={requestClose} fullWidth maxWidth="sm">
        <DialogTitle>{shownSubject || "Event details"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField select label="Event role" fullWidth value={form.role} onChange={set("role")}
                         disabled={!canEdit}>
                <MenuItem value=""><em>—</em></MenuItem>
                {ATTENDANCE_ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              <TextField select label="Status" fullWidth value={form.status} onChange={set("status")}
                         disabled={!canEdit}>
                <MenuItem value=""><em>—</em></MenuItem>
                {ATTENDANCE_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField select label="Team" fullWidth value={form.event_team_id}
                       onChange={set("event_team_id")} disabled={!canEdit || teams.length === 0}
                       helperText={teams.length === 0
                         ? "This event has no teams — add them on the event itself."
                         : "An incident allocated to this team is visible to everyone in it."}>
              <MenuItem value=""><em>No team</em></MenuItem>
              {teams.map(t => <MenuItem key={t.id} value={String(t.id)}>{t.name}</MenuItem>)}
            </TextField>
            <TextField label="Fee" type="number" value={form.fee} onChange={set("fee")}
                       disabled={!canEdit} error={feeError}
                       sx={{ maxWidth: 220 }}
                       slotProps={{
                         input: { startAdornment: <InputAdornment position="start">£</InputAdornment> },
                         htmlInput: { min: 0, step: "0.01" },
                       }}
                       helperText={feeError ? "Enter an amount of 0 or more." : "Visible to this person only."} />
            <TextField label="Shift details" fullWidth multiline minRows={3}
                       value={form.shift_details} onChange={set("shift_details")}
                       disabled={!canEdit}
                       helperText="Free text shown to this person on their My events page." />
            <TextField label="Availability notes" fullWidth multiline minRows={2}
                       value={form.availability_notes} onChange={set("availability_notes")}
                       disabled={!canEdit}
                       helperText="What they told us when saying they're available (travel, hours…). They can edit it on My events." />

            {/* What this person has been issued for this event, by item category
                then item name. Saved as you go, so Save below applies only to the
                attendance fields above. Gated on the inventory permissions, not the
                attendance ones — an allocation is part of an item's record. */}
            {/* The engagement schedule — prepared, signed and sent from here; saves
                as it goes, independently of Save below. */}
            {canContract && attendance?.id && (
              <>
                <Divider textAlign="left">Contract</Divider>
                <PersonnelContractPanel attendance={attendance} startSignal={startContract}
                                        beforeAction={saveBeforeContract}
                                        onState={setContractState} onChanged={contractSent} />
              </>
            )}

            {can("InventoryItems.view") && (
              <>
                <Divider textAlign="left">Inventory allocations</Divider>
                <Typography variant="caption" color="text.secondary">
                  Kit issued to this person for this event. Saved as you go — Save
                  below applies to the fields above only.
                </Typography>
                <AllocationManager
                  personnelId={attendance?.personnel_id}
                  eventId={attendance?.event_id}
                  canAdd={can("InventoryItems.add")}
                  canEdit={can("InventoryItems.edit")}
                  canDelete={can("InventoryItems.delete")} />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {canDelete && (
            <Button color="error" startIcon={<DeleteIcon />} disabled={busy}
                    onClick={() => setConfirmDelete(true)} sx={{ mr: "auto" }}>
              Remove from event
            </Button>
          )}
          <Button onClick={requestClose}>{canEdit ? "Cancel" : "Close"}</Button>
          {canEdit && (
            <Button variant="contained" onClick={save} disabled={busy || feeError}>Save</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Offered after saving the status as Contract Sent */}
      <Dialog open={offerContract} onClose={() => { setOfferContract(false); onClose(); }}>
        <DialogTitle>Send the engagement schedule now?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Prepare {shownSubject ? `${shownSubject}'s` : "the"} engagement schedule from this event's
            template, sign it for Shelter Stone, and email them to sign it in the app.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOfferContract(false); onClose(); }}>Not now</Button>
          <Button variant="contained"
                  onClick={() => { setOfferContract(false); setStartContract(n => n + 1); }}>
            Prepare and sign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved-changes confirmation */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to these event details. If you close now, they will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="error" variant="contained"
                  onClick={() => { setConfirmClose(false); onClose(); }}>Discard changes</Button>
        </DialogActions>
      </Dialog>

      {/* Remove-from-event confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Remove from event?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove {shownSubject ? `"${shownSubject}"` : "this"} association, along with its role,
            status, fee and shift details. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={busy}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />}
                  onClick={doDelete} disabled={busy}>Remove</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
